import { useState, useEffect, useRef } from 'react'
import imageCompression from 'browser-image-compression'


const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

const CLIENT_DEVELOPERS = [
  'Acube', 'Ahmed Al Ansari', 'Al Habtoor', 'Aldar', 'Amer Al Ghurair', 'Amina', 'Amirah', 'Anax', 'AQUA', 'Arada', 'Ary & Maz', 'AYS', 'Azizi', 'Beyond', 'Binghatti', 'Blanco', 'Bloom Properties', 'BNW', 'Burtville', 'California Village', 'CasaGrand', 'Cirrera Development', 'Citi Developers', 'Confident', 'Dalmore', 'DAMAC', 'Danube', 'Deca', 'Deyaar', 'DIFC/Savills', 'Dugasta', 'Ellington', 'EMAAR', 'Expo City Dubai', 'Golden Bridge', 'HRE Development', 'Imtiaz', 'IRTH Group', 'Leos', 'MAG', 'Manam', 'Marquis', 'Merath', 'Nakheel', 'Merass', 'Meydan', 'Dubai Holding', 'Dubai Property', 'Nexa Properties', 'Nshama', 'OBG', 'OCTA', 'Ohana', 'Oksa', 'Omniyat', 'One Development', 'One Yard', 'Peace Homes', 'Pearlshire', 'Prestige One', 'Raffles', 'Reef Luxury', 'Reportage', 'Samana', 'Segrex', 'SOBHA', 'SOL', 'Symbolic', 'Synergy', 'Takmeel', 'Tarrad', 'Tomorrow World', 'Tiger Properties', 'Union Properties', 'Urban Capital', 'Vincitore', 'Vision', 'WADAN Developments', 'Zoya'
]

// ── Constants ─────────────────────────────────────────────────────────────────

const UNIT_OPTIONS_BY_TYPE = {
  Apartment: ['Studio', '1 Bedroom', '2 Bedroom', '3 Bedroom', '4 Bedroom', '5 Bedroom'],
  Townhouse: ['2 Bedroom', '3 Bedroom', '4 Bedroom', '5 Bedroom'],
  Villa: ['3 Bedroom', '4 Bedroom', '5 Bedroom', '6 Bedroom'],
  Penthouse: ['2 Bedroom', '3 Bedroom', '4 Bedroom', '5 Bedroom'],
}



// Build auto floor plan label: "1 Bedroom Apartment", "3 Bedroom Townhouse", etc.
function makeFloorPlanLabel(unitOption, propType) {
  if (propType === 'Retail') return 'Retail Space'
  if (unitOption === 'Studio') return `Studio ${propType}`
  return `${unitOption} ${propType}`
}

export default function AdminOffplanForm({ token, project, existingDevelopers = [], existingAreas = [], onClose }) {
  const isEditing = !!project

  const [showCancelModal, setShowCancelModal] = useState(false)
  const [expandOutlook, setExpandOutlook] = useState(false)

  const handleDragStart = (e, index, type) => {
    e.dataTransfer.setData('dragIndex', index)
    e.dataTransfer.setData('dragType', type)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e, dropIndex, type, array, setArray) => {
    e.preventDefault()
    const dragType = e.dataTransfer.getData('dragType')
    if (dragType !== type) return

    const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'), 10)
    if (dragIndex === dropIndex) return

    const newArray = [...array]
    const draggedItem = newArray[dragIndex]
    newArray.splice(dragIndex, 1)
    newArray.splice(dropIndex, 0, draggedItem)
    setArray(newArray)
  }

  useEffect(() => {
    window.__adminUnsavedChanges = true
    return () => {
      window.__adminUnsavedChanges = false
    }
  }, [])

  const [formData, setFormData] = useState({
    category: 'offplan',
    name: '', developer: '', area: '', emirate: 'Dubai', community: '',
    status: 'Available', statusColor: '#22C55E', startingPrice: '', startingPriceCurrency: 'AED',
    handover: '', description: '', buildingHeight: '', retailSpace: '', investmentOutlook: '',
    paymentPlanSummary: '', unitsCount: '', googleMapsLink: '', lat: '', lng: '', youtubeLink: '',
    permitNumber: '',
  })

  // New structured state
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState([]) // e.g. ['Apartment', 'Townhouse']
  const [unitOptions, setUnitOptions] = useState({})                      // e.g. { Apartment: ['1 Bedroom', '2 Bedroom'], Townhouse: ['3 Bedroom'] }
  const [selectedAmenities, setSelectedAmenities] = useState([])
  const [globalAmenities, setGlobalAmenities] = useState([])
  const [globalPropertyTypes, setGlobalPropertyTypes] = useState([])
  const [newPropertyType, setNewPropertyType] = useState('')
  const [selectedLifestyles, setSelectedLifestyles] = useState([])
  
  const [amenitySearch, setAmenitySearch] = useState('')
  const [customAmenityName, setCustomAmenityName] = useState('')
  const [customAmenityIcon, setCustomAmenityIcon] = useState('✦')
  
  const [showDevDropdown, setShowDevDropdown] = useState(false)
  const devWrapperRef = useRef(null)

  const [showAreaDropdown, setShowAreaDropdown] = useState(false)
  const areaWrapperRef = useRef(null)

  const [showEmirateDropdown, setShowEmirateDropdown] = useState(false)
  const emirateWrapperRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (devWrapperRef.current && !devWrapperRef.current.contains(event.target)) {
        setShowDevDropdown(false)
      }
      if (areaWrapperRef.current && !areaWrapperRef.current.contains(event.target)) {
        setShowAreaDropdown(false)
      }
      if (emirateWrapperRef.current && !emirateWrapperRef.current.contains(event.target)) {
        setShowEmirateDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const [paymentPlan, setPaymentPlan] = useState([])
  const [nearbyPlaces, setNearbyPlaces] = useState([])
  const [floorPlans, setFloorPlans] = useState([])

  const [coverImage, setCoverImage] = useState(null)
  const [coverPreview, setCoverPreview] = useState(null)
  const [gallery, setGallery] = useState([])
  const [galleryPreviews, setGalleryPreviews] = useState([])
  const [existingGallery, setExistingGallery] = useState([])
  const [loading, setLoading] = useState(false)

  // Fetch global amenities list
  const fetchAmenities = async () => {
    try {
      const res = await fetch('/api/amenities')
      if (res.ok) {
        const data = await res.json()
        setGlobalAmenities(data)
      }
    } catch (err) {
      console.error('Failed to fetch amenities', err)
    }
  }

  const fetchPropertyTypes = async () => {
    try {
      const res = await fetch('/api/propertyTypes')
      if (res.ok) {
        const data = await res.json()
        setGlobalPropertyTypes(data.map(p => p.label))
      }
    } catch (err) {
      console.error('Failed to fetch property types', err)
    }
  }

  useEffect(() => {
    fetchAmenities()
    fetchPropertyTypes()
  }, [])

  useEffect(() => {
    if (project) {
      setFormData({
        category: project.category || 'offplan',
        name: project.name || '',
        developer: project.developer || '',
        area: project.area || '',
        emirate: project.emirate || 'Dubai',
        community: project.community || '',
        status: project.status || 'Available',
        statusColor: project.statusColor || '#22C55E',
        startingPrice: project.startingPrice || '',
        startingPriceCurrency: project.startingPriceCurrency || 'AED',
        handover: project.handover || '',
        description: project.description || '',
        buildingHeight: project.buildingHeight || '',
        retailSpace: project.retailSpace || '',
        investmentOutlook: project.investmentOutlook || '',
        paymentPlanSummary: project.paymentPlanSummary || '',
        unitsCount: project.unitsCount || '',
        googleMapsLink: project.googleMapsLink || '',
        lat: project.lat || '',
        lng: project.lng || '',
        youtubeLink: project.youtubeLink || '',
        permitNumber: project.permitNumber || '',
      })
      // Handle both array format and legacy comma-separated string format for amenities
      let parsedAmenities = []
      if (Array.isArray(project.amenities)) {
        parsedAmenities = project.amenities
      } else if (typeof project.amenities === 'string') {
        parsedAmenities = project.amenities.split(',').map(s => s.trim()).filter(Boolean)
      }
      setSelectedAmenities(parsedAmenities)

      setSelectedPropertyTypes(Array.isArray(project.propertyTypes) ? project.propertyTypes : [])
      setUnitOptions(project.unitOptions && typeof project.unitOptions === 'object' ? project.unitOptions : {})
      setPaymentPlan(Array.isArray(project.paymentPlan) ? project.paymentPlan : [])
      setNearbyPlaces(Array.isArray(project.nearbyPlaces) ? project.nearbyPlaces : [])
      setFloorPlans(Array.isArray(project.floorPlans) ? project.floorPlans : [])
      setFloorPlans(Array.isArray(project.floorPlans) ? project.floorPlans : [])
      setExistingGallery(Array.isArray(project.gallery) ? project.gallery : [])
      setSelectedLifestyles(Array.isArray(project.lifestyleTags) ? project.lifestyleTags : [])
    }
  }, [project])

  const handleTextChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleStatusChange = (e) => {
    const status = e.target.value
    let statusColor = '#22C55E' // Available
    if (status === 'Sold Out') statusColor = '#EF4444'
    if (status === 'Coming Soon') statusColor = '#F59E0B'
    if (status === 'Now Selling') statusColor = '#3B82F6'

    setFormData(prev => ({ ...prev, status, statusColor }))
  }

  // ── Property Type Toggle ──────────────────────────────────────────────────
  const togglePropertyType = (type) => {
    setSelectedPropertyTypes(prev => {
      const next = prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
      // Clean up unit options for removed types
      if (prev.includes(type)) {
        setUnitOptions(uo => { const n = { ...uo }; delete n[type]; return n })
        // Remove floor plan rows for this type
        setFloorPlans(fp => fp.filter(f => !f.unitType.endsWith(type) && f._propType !== type))
      }
      return next
    })
  }

  // ── Unit Option Toggle ────────────────────────────────────────────────────
  const toggleUnitOption = (propType, option) => {
    setUnitOptions(prev => {
      const current = prev[propType] || []
      const isRemoving = current.includes(option)
      const next = isRemoving ? current.filter(o => o !== option) : [...current, option]
      const newUO = { ...prev, [propType]: next }

      // Sync floor plans
      const label = makeFloorPlanLabel(option, propType)
      if (isRemoving) {
        setFloorPlans(fp => fp.filter(f => f.unitType !== label))
      } else {
        setFloorPlans(fp => {
          if (fp.some(f => f.unitType === label)) return fp
          return [...fp, { unitType: label, units: '', sizeRange: '', startingPrice: '' }]
        })
      }
      return newUO
    })
  }

  const updateFloorPlan = (index, field, value) => {
    const newPlans = [...floorPlans]
    newPlans[index][field] = value
    setFloorPlans(newPlans)
  }
  const removeFloorPlan = (index) => setFloorPlans(floorPlans.filter((_, i) => i !== index))
  const addFloorPlan = () => setFloorPlans([...floorPlans, { unitType: '', units: '', sizeRange: '', startingPrice: '' }])

  // ── Payment Plan ──────────────────────────────────────────────────────────
  const addPaymentPlan = () => setPaymentPlan([...paymentPlan, { milestone: '', percentage: '' }])
  const updatePaymentPlan = (index, field, value) => {
    const newPlan = [...paymentPlan]; newPlan[index][field] = value; setPaymentPlan(newPlan)
  }
  const removePaymentPlan = (index) => setPaymentPlan(paymentPlan.filter((_, i) => i !== index))

  // ── Nearby Places ─────────────────────────────────────────────────────────
  const addNearbyPlace = () => setNearbyPlaces([...nearbyPlaces, { place: '', distance: '' }])
  const updateNearbyPlace = (index, field, value) => {
    const newPlaces = [...nearbyPlaces]; newPlaces[index][field] = value; setNearbyPlaces(newPlaces)
  }
  const removeNearbyPlace = (index) => setNearbyPlaces(nearbyPlaces.filter((_, i) => i !== index))

  const handleCoverChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setCoverPreview(URL.createObjectURL(file))
    try {
      const compressedFile = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true })
      setCoverImage(compressedFile)
    } catch (error) {
      console.error('Error compressing cover image:', error)
      setCoverImage(file)
    }
  }

  const handleGalleryChange = async (e) => {
    const files = Array.from(e.target.files)
    if (!files.length) return

    if (gallery.length + existingGallery.length + files.length > 50) {
      alert('You can only have a maximum of 50 images in the gallery.')
      e.target.value = null
      return
    }

    setGalleryPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))])
    
    try {
      const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true }
      const compressedFiles = await Promise.all(
        files.map(f => imageCompression(f, options).catch(() => f))
      )
      setGallery(prev => [...prev, ...compressedFiles])
    } catch (error) {
      console.error('Error compressing gallery images:', error)
      setGallery(prev => [...prev, ...files])
    }
    e.target.value = null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    const payload = {
      ...formData,
      propertyTypes: selectedPropertyTypes,
      unitOptions,
      amenities: selectedAmenities,
      paymentPlan: paymentPlan.map(p => ({ ...p, percentage: p.percentage.toString().replace(/%/g, '').trim() })),
      nearbyPlaces,
      floorPlans,
      existingGallery,
      lifestyleTags: selectedLifestyles
    }

    const formDataObj = new FormData()
    formDataObj.append('data', JSON.stringify(payload))
    if (coverImage) formDataObj.append('coverImage', coverImage)
    gallery.forEach(file => formDataObj.append('gallery', file))

    try {
      const url = isEditing ? `/api/adminOffplan/${project.id}` : '/api/adminOffplan'
      const method = isEditing ? 'PUT' : 'POST'
      const res = await fetch(url, { method, headers: { 'x-admin-token': token }, body: formDataObj })
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}))
        throw new Error(errData.error || 'Failed to save project')
      }
      onClose(true)
    } catch (err) {
      alert(`Error saving project: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const sectionStyle = { background: '#fff', padding: '24px', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontFamily: FONT, fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box' }
  const selectStyle = { ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23002E53' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: '36px' }
  const labelStyle = { display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: NAVY, marginBottom: '6px' }
  const btnStyle = { padding: '8px 16px', borderRadius: '6px', border: 'none', background: `${GOLD}20`, color: GOLD, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }

  const chipBase = { padding: '7px 16px', borderRadius: '20px', border: '2px solid', fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }

  return (
    <div style={{ padding: '24px', background: '#f0f4f8', minHeight: '100vh', fontFamily: FONT }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, color: NAVY }}>{isEditing ? 'Edit Properties/Project' : 'Add New Properties/Project'}</h2>
        <button type="button" onClick={() => setShowCancelModal(true)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
      </div>

      <form 
        onSubmit={handleSubmit}
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault()
          }
        }}
      >

        {/* ── Basic Details ── */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', color: GOLD }}>Basic Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Project Name *</label>
              <input style={inputStyle} name="name" value={formData.name} onChange={handleTextChange} required />
            </div>
            <div style={{ position: 'relative' }} ref={devWrapperRef}>
              <label style={labelStyle}>Developer *</label>
              <input 
                style={inputStyle} 
                name="developer" 
                value={formData.developer} 
                onChange={handleTextChange} 
                onFocus={() => setShowDevDropdown(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') setShowDevDropdown(false) }}
                autoComplete="off"
                required 
              />
              {showDevDropdown && (
                <ul style={{
                  position: 'absolute', top: 'calc(100% - 12px)', left: 0, right: 0,
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                  maxHeight: '200px', overflowY: 'auto', zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', listStyle: 'none', padding: 0, margin: 0
                }}>
                  {[...new Set([...CLIENT_DEVELOPERS, ...existingDevelopers])]
                    .sort()
                    .filter(d => d.toLowerCase().includes((formData.developer || '').toLowerCase()))
                    .map(d => (
                      <li 
                        key={d}
                        style={{ padding: '10px 14px', cursor: 'pointer', fontFamily: FONT, fontSize: '13px', color: NAVY, borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                        onMouseDown={(e) => {
                          // use onMouseDown instead of onClick to fire before input onBlur
                          e.preventDefault()
                          handleTextChange({ target: { name: 'developer', value: d } })
                          setShowDevDropdown(false)
                        }}
                      >
                        {d}
                      </li>
                  ))}
                  {[...new Set([...CLIENT_DEVELOPERS, ...existingDevelopers])].filter(d => d.toLowerCase().includes((formData.developer || '').toLowerCase())).length === 0 && (
                    <li style={{ padding: '10px 14px', fontFamily: FONT, fontSize: '13px', color: '#94a3b8' }}>
                      Press enter to save "{formData.developer}" as new
                    </li>
                  )}
                </ul>
              )}
            </div>
            <div style={{ position: 'relative' }} ref={areaWrapperRef}>
              <label style={labelStyle}>Area *</label>
              <input 
                style={inputStyle} 
                name="area" 
                value={formData.area} 
                onChange={handleTextChange} 
                onFocus={() => setShowAreaDropdown(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') setShowAreaDropdown(false) }}
                autoComplete="off"
                required 
                placeholder="e.g. Dubai South" 
              />
              {showAreaDropdown && (
                <ul style={{
                  position: 'absolute', top: 'calc(100% - 12px)', left: 0, right: 0,
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                  maxHeight: '200px', overflowY: 'auto', zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', listStyle: 'none', padding: 0, margin: 0
                }}>
                  {existingAreas
                    .sort()
                    .filter(a => a.toLowerCase().includes((formData.area || '').toLowerCase()))
                    .map(a => (
                      <li 
                        key={a}
                        style={{ padding: '10px 14px', cursor: 'pointer', fontFamily: FONT, fontSize: '13px', color: NAVY, borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleTextChange({ target: { name: 'area', value: a } })
                          setShowAreaDropdown(false)
                        }}
                      >
                        {a}
                      </li>
                  ))}
                  {existingAreas.filter(a => a.toLowerCase().includes((formData.area || '').toLowerCase())).length === 0 && (
                    <li style={{ padding: '10px 14px', fontFamily: FONT, fontSize: '13px', color: '#94a3b8' }}>
                      Press enter to save "{formData.area}" as new
                    </li>
                  )}
                </ul>
              )}
            </div>
            <div style={{ position: 'relative' }} ref={emirateWrapperRef}>
              <label style={labelStyle}>Emirate / Country *</label>
              <input 
                style={inputStyle} 
                name="emirate" 
                value={formData.emirate} 
                onChange={handleTextChange} 
                onFocus={() => setShowEmirateDropdown(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') setShowEmirateDropdown(false) }}
                autoComplete="off"
                required 
                placeholder="e.g. Dubai" 
              />
              {showEmirateDropdown && (
                <ul style={{
                  position: 'absolute', top: 'calc(100% - 12px)', left: 0, right: 0,
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                  maxHeight: '200px', overflowY: 'auto', zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', listStyle: 'none', padding: 0, margin: 0
                }}>
                  {['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Fujairah', 'Ras Al Khaimah', 'Umm Al Quwain']
                    .filter(e => e.toLowerCase().includes((formData.emirate || '').toLowerCase()))
                    .map(e => (
                      <li 
                        key={e}
                        style={{ padding: '10px 14px', cursor: 'pointer', fontFamily: FONT, fontSize: '13px', color: NAVY, borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={(ev) => ev.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={(ev) => ev.currentTarget.style.background = '#fff'}
                        onMouseDown={(ev) => {
                          ev.preventDefault()
                          handleTextChange({ target: { name: 'emirate', value: e } })
                          setShowEmirateDropdown(false)
                        }}
                      >
                        {e}
                      </li>
                  ))}
                  {['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Fujairah', 'Ras Al Khaimah', 'Umm Al Quwain'].filter(e => e.toLowerCase().includes((formData.emirate || '').toLowerCase())).length === 0 && (
                    <li style={{ padding: '10px 14px', fontFamily: FONT, fontSize: '13px', color: '#94a3b8' }}>
                      Press enter to save "{formData.emirate}" as new
                    </li>
                  )}
                </ul>
              )}
            </div>
            <div>
              <label style={labelStyle}>Community</label>
              <input style={inputStyle} name="community" value={formData.community} onChange={handleTextChange} placeholder="e.g. Emaar South" />
            </div>
            <div>
              <label style={labelStyle}>Total Units</label>
              <input style={inputStyle} name="unitsCount" value={formData.unitsCount} onChange={handleTextChange} placeholder="e.g. 289" />
            </div>
            <div>
              <label style={labelStyle}>Starting Price *</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <select
                  name="startingPriceCurrency"
                  value={formData.startingPriceCurrency}
                  onChange={handleTextChange}
                  style={{ ...selectStyle, width: '100px', marginBottom: 0 }}
                >
                  <option value="AED">AED</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
                <input
                  style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                  name="startingPrice"
                  type="number"
                  value={formData.startingPrice}
                  onChange={handleTextChange}
                  required
                  placeholder="e.g. 1460000"
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Handover Date</label>
              <input style={inputStyle} name="handover" value={formData.handover} onChange={handleTextChange} placeholder="e.g. Q4 2026" />
            </div>
            <div>
              <label style={labelStyle}>Payment Plan Summary</label>
              <input style={inputStyle} name="paymentPlanSummary" value={formData.paymentPlanSummary} onChange={handleTextChange} placeholder="e.g. 80/20" />
            </div>
            <div>
              <label style={labelStyle}>Category</label>
              <select style={selectStyle} name="category" value={formData.category} onChange={handleTextChange}>
                <option value="offplan">New Properties/Project</option>
                <option value="buy">Buy (Resale)</option>
                <option value="rent">Rent</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={selectStyle} name="status" value={formData.status} onChange={handleStatusChange}>
                <option value="Available">Available (Green)</option>
                <option value="Now Selling">Now Selling (Blue)</option>
                <option value="Coming Soon">Coming Soon (Orange)</option>
                <option value="Sold Out">Sold Out (Red)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Permit Number</label>
              <input style={inputStyle} name="permitNumber" value={formData.permitNumber} onChange={handleTextChange} placeholder="e.g. 1234567890" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, height: '120px' }} name="description" value={formData.description} onChange={handleTextChange} />
          </div>
        </div>

        {/* ── Property Types & Unit Options ── */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 4px', color: GOLD }}>Property Types</h3>
          <p style={{ fontFamily: FONT, fontSize: '12px', color: '#6b7280', margin: '0 0 16px' }}>
            Select all that apply. Unit types and floor plan rows will be generated automatically.
          </p>

          {/* Property Type chips */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {globalPropertyTypes.map(type => {
              const isActive = selectedPropertyTypes.includes(type)
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => togglePropertyType(type)}
                  style={{
                    ...chipBase,
                    borderColor: isActive ? GOLD : '#d1d5db',
                    background: isActive ? GOLD : '#fff',
                    color: isActive ? '#fff' : '#374151',
                  }}
                >
                  {type}
                </button>
              )
            })}
          </div>

          {/* Add custom property type */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', maxWidth: '300px' }}>
            <input
              style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
              placeholder="Add new type..."
              value={newPropertyType}
              onChange={e => setNewPropertyType(e.target.value)}
            />
            <button
              type="button"
              style={btnStyle}
              onClick={async () => {
                const name = newPropertyType.trim()
                if (name) {
                  try {
                    const res = await fetch('/api/propertyTypes', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ name })
                    })
                    if (res.ok) {
                      await fetchPropertyTypes()
                      setSelectedPropertyTypes(prev => prev.includes(name) ? prev : [...prev, name])
                      setNewPropertyType('')
                    } else {
                      const data = await res.json()
                      alert(data.error || 'Failed to add property type')
                    }
                  } catch (e) {
                    alert('Error adding property type')
                  }
                }
              }}
            >
              Add
            </button>
          </div>

          {/* Per-type unit options */}
          {selectedPropertyTypes.filter(t => t !== 'Retail').map(propType => (
            <div key={propType} style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, marginBottom: '10px' }}>
                {propType} — Unit Types
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {(UNIT_OPTIONS_BY_TYPE[propType] || []).map(option => {
                  const isActive = (unitOptions[propType] || []).includes(option)
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleUnitOption(propType, option)}
                      style={{
                        ...chipBase,
                        padding: '5px 14px',
                        fontSize: '12px',
                        borderColor: isActive ? NAVY : '#d1d5db',
                        background: isActive ? NAVY : '#fff',
                        color: isActive ? '#fff' : '#374151',
                      }}
                    >
                      {option}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Retail Space — only shown if Retail is selected */}
          {selectedPropertyTypes.includes('Retail') && (
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, marginBottom: '10px' }}>
                Retail Space Details
              </div>
              <label style={labelStyle}>Retail Space Description</label>
              <input style={{ ...inputStyle, marginBottom: 0 }} name="retailSpace" value={formData.retailSpace} onChange={handleTextChange} placeholder="e.g. 3,789.65 sq.ft across 12 units" />
            </div>
          )}
        </div>

        {/* ── Specific Facts ── */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', color: GOLD }}>Specific Facts</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Building Height</label>
              <input style={inputStyle} name="buildingHeight" value={formData.buildingHeight} onChange={handleTextChange} placeholder="e.g. G + 48 Floors" />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Investment Outlook</label>
                <button type="button" onClick={() => setExpandOutlook(true)} style={{ background: 'transparent', border: 'none', color: NAVY, fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>
                  View Bigger
                </button>
              </div>
              <textarea style={{ ...inputStyle, height: '80px', marginBottom: 0 }} name="investmentOutlook" value={formData.investmentOutlook} onChange={handleTextChange} placeholder="Why is this a good investment?" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>YouTube Video Link</label>
              <input style={inputStyle} name="youtubeLink" value={formData.youtubeLink} onChange={handleTextChange} placeholder="e.g. https://www.youtube.com/watch?v=..." />
            </div>
          </div>
        </div>

        {/* ── Lifestyle Tags ── */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', color: GOLD }}>Lifestyle Tags</h3>
          <p style={{ fontFamily: FONT, fontSize: '12px', color: '#6b7280', margin: '0 0 12px' }}>
            Select the tags that best describe this project. These appear on the property card and power the search filters.
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { id: 'waterfront', label: '🌊 Waterfront' },
              { id: 'family',     label: '👨‍👩‍👧‍👦 Family' },
              { id: 'luxury',     label: '💎 Luxury' },
              { id: 'investment', label: '📈 Investment' },
              { id: 'golf',       label: '⛳ Golf Course' },
              { id: 'urban',      label: '🏙️ Urban' },
              { id: 'nature',     label: '🌳 Nature' }
            ].map(l => {
              const isActive = selectedLifestyles.includes(l.id)
              return (
                <button
                  key={l.id} type="button"
                  onClick={() => setSelectedLifestyles(prev => isActive ? prev.filter(x => x !== l.id) : [...prev, l.id])}
                  style={{
                    ...chipBase, padding: '6px 14px', fontSize: '13px',
                    borderColor: isActive ? NAVY : '#d1d5db',
                    background: isActive ? NAVY : '#fff',
                    color: isActive ? '#fff' : '#374151',
                  }}
                >
                  {l.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* ── Premium Amenities ── */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', color: GOLD }}>Premium Amenities</h3>

          {/* Selected Amenities (includes legacy/custom ones) */}
          {selectedAmenities.length > 0 && (
            <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase' }}>Selected Amenities (Click to remove)</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {selectedAmenities.map(amenity => (
                  <button
                    key={amenity}
                    type="button"
                    onClick={() => setSelectedAmenities(prev => prev.filter(a => a !== amenity))}
                    style={{ 
                      ...chipBase, 
                      background: NAVY, 
                      color: '#fff', 
                      borderColor: NAVY, 
                      padding: '8px 16px', 
                      fontSize: '13px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px' 
                    }}
                  >
                    <span>{amenity}</span> 
                    <span style={{ fontSize: '14px', opacity: 0.8, marginLeft: '4px' }}>✕</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search and Custom Add */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <input
              style={{ ...inputStyle, marginBottom: 0, flex: 2 }}
              placeholder="Search amenities..."
              value={amenitySearch}
              onChange={e => setAmenitySearch(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '8px', flex: 2 }}>
              <input
                style={{ ...inputStyle, marginBottom: 0, width: '60px', textAlign: 'center' }}
                placeholder="Icon"
                value={customAmenityIcon}
                onChange={e => setCustomAmenityIcon(e.target.value)}
                maxLength={2}
              />
              <input
                style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                placeholder="New Custom Amenity..."
                value={customAmenityName}
                onChange={e => setCustomAmenityName(e.target.value)}
              />
              <button
                type="button"
                style={btnStyle}
                onClick={async () => {
                  if (customAmenityName.trim()) {
                    try {
                      const res = await fetch('/api/amenities', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: customAmenityName.trim(), icon: customAmenityIcon.trim() || '✦' })
                      });
                      if (res.ok) {
                        await fetchAmenities() // refresh the list
                        setSelectedAmenities(prev => [...prev, customAmenityName.trim()])
                        setCustomAmenityName('')
                        setCustomAmenityIcon('✦')
                      } else {
                        const data = await res.json();
                        alert(data.error || 'Failed to add amenity');
                      }
                    } catch (e) {
                      alert('Error adding amenity');
                    }
                  }
                }}
              >
                Add
              </button>
            </div>
          </div>

          {/* Standard Amenities List */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', maxHeight: '200px', overflowY: 'auto', padding: '4px' }}>
            {globalAmenities.filter(a => a.name.toLowerCase().includes(amenitySearch.toLowerCase())).map(amenityObj => {
              const isActive = selectedAmenities.includes(amenityObj.name)
              if (isActive) return null; // hide from list if already selected
              return (
                <div key={amenityObj.name} style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedAmenities(prev => [...prev, amenityObj.name])}
                    style={{ 
                      ...chipBase, 
                      padding: '8px 16px', 
                      fontSize: '13px', 
                      borderColor: '#d1d5db', 
                      background: '#fff', 
                      color: '#374151', 
                      paddingRight: amenityObj.isCustom ? '36px' : '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>{amenityObj.icon}</span>
                    <span>{amenityObj.name}</span>
                  </button>
                  {amenityObj.isCustom && (
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete custom amenity "${amenityObj.name}" globally?`)) {
                          try {
                            const res = await fetch(`/api/amenities/${encodeURIComponent(amenityObj.name)}`, { method: 'DELETE' });
                            if (res.ok) {
                              await fetchAmenities();
                              setSelectedAmenities(prev => prev.filter(a => a !== amenityObj.name));
                            }
                          } catch (err) { }
                        }
                      }}
                      style={{ 
                        position: 'absolute', 
                        right: '8px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        background: 'transparent', 
                        border: 'none', 
                        color: '#ef4444', 
                        fontSize: '16px', 
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Arrays Section ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>

          {/* Payment Plan */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: GOLD }}>Payment Plan</h3>
              <button type="button" onClick={addPaymentPlan} style={btnStyle}>+ Add Stage</button>
            </div>
            {paymentPlan.map((p, idx) => (
              <div 
                key={idx} 
                draggable
                onDragStart={(e) => handleDragStart(e, idx, 'paymentPlan')}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx, 'paymentPlan', paymentPlan, setPaymentPlan)}
                style={{ display: 'grid', gridTemplateColumns: '16px 1fr 90px auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}
              >
                <div style={{ color: '#cbd5e1', cursor: 'grab', textAlign: 'center', fontSize: '18px' }}>⋮⋮</div>
                <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Milestone (e.g. On Booking)" value={p.milestone} onChange={e => updatePaymentPlan(idx, 'milestone', e.target.value)} />
                <input style={{ ...inputStyle, marginBottom: 0, width: '100%' }} placeholder="% (e.g. 20)" value={p.percentage} onChange={e => updatePaymentPlan(idx, 'percentage', e.target.value)} />
                <button type="button" onClick={() => removePaymentPlan(idx)} style={{ ...btnStyle, background: '#fee2e2', color: '#ef4444' }}>X</button>
              </div>
            ))}
          </div>

          {/* Location & Connectivity */}
          <div style={sectionStyle}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: GOLD }}>Location & Connectivity</h3>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Live Map Preview</label>
              <div style={{ height: '300px', width: '100%', marginBottom: '16px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <iframe
                  title="Admin Map Preview"
                  width="100%"
                  height="100%"
                  style={{ border: 'none', display: 'block' }}
                  loading="lazy"
                  src={(formData.lat && formData.lng)
                    ? `https://maps.google.com/maps?q=${formData.lat},${formData.lng}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                    : `https://maps.google.com/maps?q=${encodeURIComponent((formData.name || '') + ', ' + (formData.area || '') + ', Dubai')}&t=&z=13&ie=UTF8&iwloc=&output=embed`
                  }
                />
              </div>
            </div>

            <div style={{ marginBottom: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={{ ...labelStyle, color: NAVY }}>Exact Coordinates (optional)</label>
              <p style={{ fontFamily: FONT, fontSize: '11px', color: '#6b7280', margin: '0 0 12px' }}>
                These coordinates will force a precise red pin on the visual map.<br />
                <strong>How to get them:</strong> Open Google Maps on your computer, right-click the exact location marker, and click the numbers at the top (e.g. 25.197, 55.274) to copy them.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <input style={{ ...inputStyle, marginBottom: 0 }} name="lat" value={formData.lat} onChange={handleTextChange} placeholder="Latitude (e.g. 25.1972)" />
                </div>
                <div>
                  <input style={{ ...inputStyle, marginBottom: 0 }} name="lng" value={formData.lng} onChange={handleTextChange} placeholder="Longitude (e.g. 55.2744)" />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Exact Google Maps Link</label>
              <p style={{ fontFamily: FONT, fontSize: '11px', color: '#6b7280', margin: '0 0 8px' }}>
                Paste a Share Link (e.g., https://maps.app.goo.gl/...) to add an "Open in Google Maps" button for buyers.
              </p>
              <input
                style={inputStyle}
                name="googleMapsLink"
                value={formData.googleMapsLink}
                onChange={handleTextChange}
                placeholder="https://maps.app.goo.gl/..."
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Nearby Places (Distances)</label>
              <button type="button" onClick={addNearbyPlace} style={btnStyle}>+ Add Nearby Place</button>
            </div>
            {nearbyPlaces.map((n, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Place (e.g. Dubai Mall)" value={n.place} onChange={e => updateNearbyPlace(idx, 'place', e.target.value)} />
                <input style={{ ...inputStyle, marginBottom: 0, width: '150px' }} placeholder="Time (e.g. 15 mins)" value={n.distance} onChange={e => updateNearbyPlace(idx, 'distance', e.target.value)} />
                <button type="button" onClick={() => removeNearbyPlace(idx)} style={{ ...btnStyle, background: '#fee2e2', color: '#ef4444' }}>X</button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Unit Types / Floor Plans ── */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <h3 style={{ margin: 0, color: GOLD }}>Unit Types & Floor Plans</h3>
            <button type="button" onClick={addFloorPlan} style={btnStyle}>+ Add Row Manually</button>
          </div>
          <p style={{ fontFamily: FONT, fontSize: '12px', color: '#6b7280', margin: '0 0 16px' }}>
            Rows are auto-generated from your property type selections above. Fill in the details for each.
          </p>
          {floorPlans.length === 0 && (
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center', fontFamily: FONT, fontSize: '13px', color: '#9ca3af' }}>
              Select property types above to auto-generate rows, or add manually.
            </div>
          )}
          {floorPlans.map((f, idx) => (
            <div 
              key={idx} 
              draggable
              onDragStart={(e) => handleDragStart(e, idx, 'floorPlans')}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, idx, 'floorPlans', floorPlans, setFloorPlans)}
              style={{ display: 'grid', gridTemplateColumns: '16px 2fr 1fr 1fr 1fr 1.5fr auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}
            >
              <div style={{ color: '#cbd5e1', cursor: 'grab', textAlign: 'center', fontSize: '18px' }}>⋮⋮</div>
              <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Type (e.g. 1 Bed Apt)" value={f.unitType} onChange={e => updateFloorPlan(idx, 'unitType', e.target.value)} />
              <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Units" type="number" value={f.units || ''} onChange={e => updateFloorPlan(idx, 'units', e.target.value)} />
              <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Min Size (Sqft)" type="number" value={f.minSize || ''} onChange={e => updateFloorPlan(idx, 'minSize', e.target.value)} />
              <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Max Size (Sqft)" type="number" value={f.maxSize || ''} onChange={e => updateFloorPlan(idx, 'maxSize', e.target.value)} />
              <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Starting Price (AED)" type="number" value={f.startingPrice || ''} onChange={e => updateFloorPlan(idx, 'startingPrice', e.target.value)} />
              <button type="button" onClick={() => removeFloorPlan(idx)} style={{ ...btnStyle, background: '#fee2e2', color: '#ef4444' }}>X</button>
            </div>
          ))}
        </div>

        {/* ── Images ── */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', color: GOLD }}>Images</h3>
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Cover Image (Replaces existing if selected)</label>
            <input type="file" onChange={handleCoverChange} accept="image/*" />
            {/* Preview new selection */}
            {coverPreview && (
              <div style={{ position: 'relative', display: 'inline-block', marginTop: '10px' }}>
                <img src={coverPreview} alt="cover preview" style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: `2px solid ${GOLD}` }} />
                <button type="button" onClick={() => { setCoverImage(null); setCoverPreview(null); }} style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'red', color: 'white', borderRadius: '50%', border: 'none', width: '20px', height: '20px', cursor: 'pointer', fontSize: '10px' }}>X</button>
              </div>
            )}
            {!coverPreview && isEditing && project.cover_image?.url && (
              <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={project.cover_image.url} alt="current cover" style={{ width: '80px', height: '55px', objectFit: 'cover', borderRadius: '6px' }} />
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Current cover</span>
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Gallery Images — select multiple at once</label>
            <input type="file" multiple onChange={handleGalleryChange} accept="image/*" />
            {/* Preview new files */}
            {galleryPreviews.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {galleryPreviews.map((src, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <img src={src} alt="" style={{ width: '70px', height: '52px', objectFit: 'cover', borderRadius: '6px', border: `2px solid ${GOLD}` }} />
                    <button type="button" onClick={() => {
                      setGallery(prev => prev.filter((_, i) => i !== idx));
                      setGalleryPreviews(prev => prev.filter((_, i) => i !== idx));
                    }} style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'red', color: 'white', borderRadius: '50%', border: 'none', width: '20px', height: '20px', cursor: 'pointer', fontSize: '10px' }}>X</button>
                  </div>
                ))}
              </div>
            )}
            {/* Existing saved gallery */}
            {isEditing && existingGallery.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <div style={{ fontFamily: FONT, fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Saved Gallery</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {existingGallery.map((img, idx) => (
                    <div 
                      key={idx} 
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx, 'gallery')}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx, 'gallery', existingGallery, setExistingGallery)}
                      style={{ position: 'relative', cursor: 'grab' }}
                    >
                      <img src={img.url} alt="" style={{ width: '70px', height: '52px', objectFit: 'cover', borderRadius: '6px' }} />
                      <button type="button" onClick={() => setExistingGallery(existingGallery.filter((_, i) => i !== idx))} style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'red', color: 'white', borderRadius: '50%', border: 'none', width: '20px', height: '20px', cursor: 'pointer', fontSize: '10px' }}>X</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <button type="submit" disabled={loading} style={{ width: '100%', padding: '16px', borderRadius: '8px', border: 'none', background: NAVY, color: '#fff', fontSize: '16px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: FONT, opacity: loading ? 0.8 : 1 }}>
          {loading ? 'Uploading Images & Saving...' : 'Save Project'}
        </button>
        {loading && (
          <p style={{ textAlign: 'center', fontSize: '13px', color: '#6b7280', marginTop: '12px', fontFamily: FONT, animation: 'pulse 2s infinite' }}>
            Please do not close this window. Uploading multiple high-quality images may take a moment...
            <style>
              {`
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.5; }
                }
              `}
            </style>
          </p>
        )}
      </form>

      {/* ── Investment Outlook Modal ── */}
      {expandOutlook && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', width: '90%', maxWidth: '800px', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: NAVY }}>Investment Outlook</h3>
              <button onClick={() => setExpandOutlook(false)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            </div>
            <textarea 
              style={{ ...inputStyle, height: '400px', resize: 'vertical', fontSize: '16px', lineHeight: '1.6' }} 
              name="investmentOutlook" 
              value={formData.investmentOutlook} 
              onChange={handleTextChange} 
              placeholder="Why is this a good investment?" 
            />
            <button type="button" onClick={() => setExpandOutlook(false)} style={{ ...btnStyle, background: NAVY, color: '#fff', alignSelf: 'flex-end', marginTop: '16px', padding: '12px 24px' }}>Done</button>
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal Overlay */}
      {showCancelModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,16,32,0.6)', backdropFilter: 'blur(8px)' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', border: `1px solid ${GOLD}40`, animation: 'cardIn 0.2s ease' }}>
            <div style={{ fontSize: '36px', marginBottom: '16px', textAlign: 'center' }}>⚠️</div>
            <h3 style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: NAVY, textAlign: 'center', margin: '0 0 12px' }}>Discard Changes?</h3>
            <p style={{ fontFamily: FONT, fontSize: '14px', color: '#64748b', textAlign: 'center', margin: '0 0 24px', lineHeight: 1.6 }}>Are you sure you want to cancel? All unsaved information for this project will be lost permanently.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowCancelModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #d1d5db', background: '#fff', color: NAVY, fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafb'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>Keep Editing</button>
              <button type="button" onClick={() => onClose()} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#fff', fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(220,38,38,0.2)', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'} onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}>Discard</button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
